import React from "react";
import styled from "styled-components";
import { SpaceProps, space, FlexboxProps, flexbox } from "styled-system";

import Box from "../Box/Box";
import Link from "../Link/Link";
import Logo from "../Logo/Logo";
import Typography from "../Typography/Typography";
import ResponsiveLayout from "../ResponsiveLayout/ResponsiveLayout";
import useFetch from "react-fetch-hook";
import { buildLegalInfo } from "../LegalInfoBuilder";

export interface CareersFooterProps {
	/**
  - urls object has the following props
  - legalInfo: string; // Url to get the legal info data
 */
	urls: {
		legalInfo: string;
	};
}
const Footer = styled(Box)`
	overflow: hidden;
	box-sizing: border-box;
	position: relative;
	min-height: 300px;
`;

const StyledLink = styled(Link)<SpaceProps>`
	${space}
`;

const LegalInfoContainer = styled(Typography)<FlexboxProps>`
	${flexbox}
`;

const LegalItem = styled.li`
	display: flex;
`;

const defaultData = {
	"@name": "Careers-Footer",
	"@path": "/UK/Careers-Footer",
	"@id": "90a97fcc-5b36-4be5-a547-8da9b0906a08",
	"@nodeType": "mgnl:folder",
	name: "Careers-Footer",
	"Terms-of-Use": {
		"@name": "Terms-of-Use",
		"@path": "/UK/Careers-Footer/Terms-of-Use",
		"@id": "46d4cc4f-6eff-4b43-a729-601101c41623",
		"@nodeType": "mgnl:menuLink",
		label: "Terms of Use",
		name: "Terms of Use",
		brandName: "BCAUK",
		isCategory: false,
		link: "https://www.bca.co.uk/legal/terms-conditions/website-terms",
		isVisible: true,
		openInNewWindow: false,
		"@nodes": [],
	},
	"Privacy-Policy": {
		"@name": "Privacy-Policy",
		"@path": "/UK/Careers-Footer/Privacy-Policy",
		"@id": "dfd67fea-359c-447a-9572-51bdfe142449",
		"@nodeType": "mgnl:menuLink",
		label: "Privacy Policy",
		name: "Privacy Policy",
		brandName: "BCAUK",
		isCategory: false,
		link: "https://www.bca.co.uk/legal/privacy-policy",
		isVisible: true,
		openInNewWindow: false,
		"@nodes": [],
	},
	"Modern-Slavery-Act": {
		"@name": "Modern-Slavery-Act",
		"@path": "/UK/Careers-Footer/Modern-Slavery-Act",
		"@id": "3cc26e40-cfc2-4eb4-9911-6257d080e87a",
		"@nodeType": "mgnl:menuLink",
		label: "Modern Slavery Act",
		name: "Modern Slavery Act",
		brandName: "BCAUK",
		isCategory: false,
		link: "https://www.constellationautomotive.com/corporate-responsibility/modern-slavery-act",
		isVisible: true,
		openInNewWindow: false,
		"@nodes": [],
	},
	"@nodes": ["Terms-of-Use", "Privacy-Policy", "Modern-Slavery-Act"],
};

const Separator = () => (
	<Typography
		display={{ _: "none", tablet: "block" }}
		variant="body"
		htmlTag="span"
		color="primary-white"
		mx={3}
		css={`
			user-select: none;
		`}
	>
		•
	</Typography>
);

const CompanyDetails = ({ urls: { legalInfo } }: CareersFooterProps) => {
	const { data, error } = useFetch(legalInfo);
	const legalData = error ? defaultData : data;
	const legalItems = buildLegalInfo(legalData);
	const copyrightYear = new Date().getFullYear();

	return (
		<>
			<Typography
				variant="body"
				color="primary-white"
				m={0}
				mt={{ _: 68, tablet: 60 }}
				textAlign="left"
				aria-label="copyright info"
			>
				© {copyrightYear} BCA. All Rights Reserved
			</Typography>
			<LegalInfoContainer
				variant="body"
				color="primary-white"
				display="flex"
				mt={{ _: 8, tablet: 4 }}
				mb={{ _: 8, tablet: 4 }}
				textAlign="left"
				justifyContent="flex-start"
				flexDirection={{ _: "column", tablet: "row" }}
				flexWrap="wrap"
				htmlTag="ul"
				padding={0}
			>
				{legalItems
					.filter((item) => item.isVisible)
					.map((item: any, index: number) => {
						const isLast = index === legalItems.length - 1;
						const bottomMargin = isLast ? 0 : { _: 4, tablet: 0 };

						return (
							<LegalItem key={item.id}>
								{index !== 0 && <Separator />}
								<StyledLink
									href={item.link}
									target={item.openInNewWindow ? "_blank" : "_top"}
									mb={bottomMargin}
								>
									{item.label}
								</StyledLink>
							</LegalItem>
						);
					})}
			</LegalInfoContainer>

			<Typography
				variant="body"
				color="lightest-grey"
				m={0}
				textAlign="left"
				aria-label="legal info"
			>
				British Car Auctions Limited, registered in England and Wales No.
				00438886
				<br />
				Registered office: Form 2, 18 Bartley Wood Business Park, Bartley Way,
				Hook, Hampshire, RG27 9XA, United Kingdom
				<br />
				VAT No. GB 188155238.
			</Typography>
		</>
	);
};

const CareersFooter = (props: CareersFooterProps) => (
	<Footer
		height="auto"
		width="100%"
		display="flex"
		flex-direction="row"
		align-items="center"
		justifyContent="flex-start"
		pt={44}
		pb={44}
		backgroundColor="footer-blue"
		as="footer"
	>
		<ResponsiveLayout>
			<Logo aria-label="BCA Logo" />
			<CompanyDetails {...props} />
		</ResponsiveLayout>
	</Footer>
);

export default CareersFooter;
